import DossierSteps from "../DossierSteps";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Checkbox, CircularProgress, Drawer, FormControlLabel, IconButton, Typography } from "@mui/material";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import MplusApi from "Services/Mplus";
import { apiErrorMessage, financingReached, formatDate, formatDateTime, isSigned, openContractPdf, unwrap, unwrapList, useHasRole } from "../mplusUtils";
import useStyles from "./style";

const CODE_LENGTH = 6;
const RESEND_DELAY = 60;

// Badge d'un contrat : pending_signature | signed | cancelled.
const contractBadge = (contract) => {
  const status = (contract?.status || "").toString().toLowerCase();
  if (status === "signed") return { label: "Signé", tone: "signed" };
  if (status === "cancelled") return { label: "Annulé", tone: "cancelled" };
  return { label: contract?.viewed_at ? "À signer · lu" : "À signer", tone: "waiting" };
};

const SIGNER_TYPES = { DRIVER: "Chauffeur", USER: "Utilisateur", BUSINESS: "Entreprise", GUARANTOR: "Garant" };
const VERSION_KINDS = { GENERATED: "Généré", SIGNED: "Signé" };

const Contracts = ({ application, financing, notify, onBack, onNext, onReload }) => {
  const classes = useStyles();
  const hasRole = useHasRole();
  const delivered = financingReached(financing?.status, "ACTIVE");
  const canGenerate = ["SUBMITTED", "LOAN_CREATED", "LOAN_APPROVED"].includes(financing?.status);
  const generationBlockedMessage = delivered
    ? "Ce dossier est déjà livré ou soldé. La génération de nouveaux contrats n'est plus autorisée après la livraison."
    : "La génération des contrats est indisponible pour le statut actuel de ce dossier.";
  const [contracts, setContracts] = useState({ sale: null, guarantee: null });
  // Tiroir d'historique : { contract, title, versions, loading }
  const [history, setHistory] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState("");
  const [withGuarantee, setWithGuarantee] = useState(true);
  // Signature en cours : { key: "sale" | "guarantee", sentTo, expiresAt }
  const [signing, setSigning] = useState(null);
  const [code, setCode] = useState(Array(CODE_LENGTH).fill(""));
  const [approved, setApproved] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const inputs = useRef([]);

  const loadContracts = useCallback(async () => {
    if (!financing?.id) return;
    setLoading(true);
    setLoadError(false);
    setVersions([]);
    try {
      const data = unwrap(await MplusApi.getFinancingContracts(financing.id)) || {};
      const sale = data.sale_contract || null;
      setContracts({ sale, guarantee: data.guarantee_contract || null });
      if (sale?.id) {
        try {
          setVersions(unwrapList(await MplusApi.getContractVersions(sale.id)));
        } catch (error) {
          setVersions([]);
        }
      }
    } catch (error) {
      setLoadError(true);
      notify(apiErrorMessage(error, "Impossible de charger les contrats."), "error");
    } finally {
      setLoading(false);
    }
  }, [financing, notify]);

  useEffect(() => {
    loadContracts();
  }, [loadContracts]);

  useEffect(() => {
    if (countdown <= 0) return undefined;
    const timer = setTimeout(() => setCountdown((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const run = async (label, request) => {
    setBusy(label);
    try {
      return await request();
    } catch (error) {
      notify(apiErrorMessage(error, "L'opération a échoué."), "error");
      return null;
    } finally {
      setBusy("");
    }
  };

  const handleGenerate = () => run("generate", async () => {
    if (!canGenerate || !hasRole("FINANCE")) return;
    await MplusApi.runFinancingAction(financing.id, "generate-contracts", { with_guarantee: withGuarantee });
    notify("Contrats générés.", "success");
    await loadContracts();
  });

  const handleRead = (contract, version) => run(`read-${contract.id}-${version || ""}`, async () => {
    await openContractPdf(contract.id, version);
    loadContracts();
  });

  const requestCode = (key) => run(`code-${key}`, async () => {
    const contract = contracts[key];
    const data = unwrap(await MplusApi.requestContractSignature(contract.id)) || {};
    setSigning({ key, sentTo: data.sent_to, expiresAt: data.expires_at });
    setCode(Array(CODE_LENGTH).fill(""));
    setApproved(false);
    setCountdown(RESEND_DELAY);
    notify(`Code envoyé${data.sent_to ? ` au ${data.sent_to}` : ""}.`, "success");
    setTimeout(() => inputs.current[0]?.focus(), 50);
  });

  const handleSign = () => run("sign", async () => {
    const contract = contracts[signing.key];
    await MplusApi.signContract(contract.id, code.join(""));
    notify("Contrat signé.", "success");
    setSigning(null);
    await loadContracts();
    onReload();
  });

  const handleDigit = (index, value) => {
    const digits = value.replace(/\D/g, "");
    if (!digits) {
      setCode((previous) => previous.map((digit, i) => (i === index ? "" : digit)));
      return;
    }
    // Collage d'un code complet ou saisie chiffre par chiffre.
    setCode((previous) => {
      const next = [...previous];
      digits.split("").slice(0, CODE_LENGTH - index).forEach((digit, offset) => { next[index + offset] = digit; });
      return next;
    });
    inputs.current[Math.min(index + digits.length, CODE_LENGTH - 1)]?.focus();
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !code[index] && index > 0) inputs.current[index - 1]?.focus();
  };

  const { sale, guarantee } = contracts;
  const allSigned = isSigned(sale) && (!guarantee || isSigned(guarantee));
  const saleBadge = contractBadge(sale);
  const guaranteeBadge = contractBadge(guarantee);

  const openHistory = async (contract, title) => {
    setHistory({ contract, title, versions: [], loading: true });
    try {
      const list = unwrapList(await MplusApi.getContractVersions(contract.id));
      setHistory({ contract, title, versions: list, loading: false });
    } catch (error) {
      notify(apiErrorMessage(error, "Impossible de charger les versions."), "error");
      setHistory({ contract, title, versions: [], loading: false });
    }
  };
  const badgeClass = (tone) => (tone === "signed" ? classes.signedBadge : tone === "cancelled" ? classes.cancelledBadge : classes.waitingBadge);
  const signedVersion = versions.find((version) => /sign/i.test(version.kind || ""));

  const renderContractCard = (key, contract, title, badge, description, isGuarantee) => (
    <Box className={classes.card}>
      <Box className={classes.contractHeading}>
        <DescriptionOutlinedIcon />
        <Box>
          <Typography className={classes.contractTitle}>{title}</Typography>
          <span className={badgeClass(badge.tone)}>
            {isGuarantee && badge.tone === "waiting" ? <StorefrontOutlinedIcon sx={{ fontSize: 15 }} /> : badge.tone === "signed" ? <CheckCircleRoundedIcon sx={{ fontSize: 15 }} /> : <VisibilityOutlinedIcon sx={{ fontSize: 15 }} />}
            {isGuarantee && badge.tone === "waiting" ? "À signer en agence" : badge.label}
          </span>
        </Box>
      </Box>
      <Box className={classes.contractMeta}>
        <span>Référence</span><b>{contract.reference_number || "--"}</b>
        <span>Version courante</span><b>{contract.current_version != null ? `v${contract.current_version}` : "--"}</b>
        <span>Créé le</span><b>{formatDate(contract.created_at)}</b>
        <span>Lu le</span><b>{formatDateTime(contract.viewed_at)}</b>
        <span>Signé le</span><b>{formatDateTime(contract.signed_at)}</b>
        <span>Signataire</span><b>{contract.signer?.name ? `${contract.signer.name}${contract.signer.type ? ` (${SIGNER_TYPES[contract.signer.type] || contract.signer.type})` : ""}` : "--"}</b>
      </Box>
      <Typography className={classes.contractDescription}>{contract.title || description}</Typography>
      <Box className={`${classes.contractActions} ${isGuarantee ? classes.agencyActions : ""}`}>
        <Button className={classes.pdfButton} startIcon={<DownloadOutlinedIcon />} disabled={Boolean(busy)} onClick={() => handleRead(contract)}>Lire le PDF</Button>
        <Button className={classes.pdfButton} startIcon={<HistoryRoundedIcon />} onClick={() => openHistory(contract, title)}>Versions</Button>
        {!isSigned(contract) && badge.tone !== "cancelled" && (
          <Button className={classes.smsButton} startIcon={<SmsOutlinedIcon />} disabled={Boolean(busy)} onClick={() => requestCode(key)}>
            {busy === `code-${key}` ? "Envoi…" : isGuarantee ? "Signer en agence" : "Envoyer le code SMS"}
          </Button>
        )}
      </Box>
      {isGuarantee && (
        <Box className={classes.infoNotice}><InfoOutlinedIcon /><span>Le garant signe avec un agent en agence.<br />Sa présence physique est obligatoire.</span></Box>
      )}
    </Box>
  );

  return (
    <Box className={classes.page}>
      <Box className={classes.header}>
        <Box className={classes.titleArea}>
          <Box className={classes.breadcrumb}>Gestion des dossiers <span>›</span> {application?.reference}</Box>
          <Box className={classes.headingLine}>
            <Typography className={classes.pageTitle}>Demande {application?.reference}</Typography>
            <span className={classes.validatedBadge}>{delivered ? "Dossier livré ou soldé" : allSigned ? "Contrats signés" : "Financement validé"}</span>
          </Box>
        </Box>
        <DossierSteps activeStep={3} />
      </Box>

      {loading ? (
        <Box className={classes.loaderBox}><CircularProgress size={28} /></Box>
      ) : loadError ? (
        <Box className={`${classes.card} ${classes.emptyCard}`}>
          <Typography className={classes.sectionTitle}>Contrats indisponibles</Typography>
          <Typography className={classes.instructions}>Impossible de charger les contrats du dossier. Réessayez pour consulter les contrats existants.</Typography>
          <Button className={classes.confirmButton} onClick={loadContracts}>Réessayer</Button>
        </Box>
      ) : !sale ? (
        <Box className={`${classes.card} ${classes.emptyCard}`}>
          <Typography className={classes.sectionTitle}>Aucun contrat généré</Typography>
          {canGenerate ? (
            <>
              <Typography className={classes.instructions}>Les contrats sont générés avec l'échéancier réel du prêt approuvé.</Typography>
              <FormControlLabel
                className={classes.approvalCheck}
                control={<Checkbox size="small" checked={withGuarantee} onChange={(event) => setWithGuarantee(event.target.checked)} />}
                label="Inclure l'acte de caution (garant)"
              />
              {hasRole("FINANCE") ? (
                <Button className={classes.confirmButton} disabled={Boolean(busy)} onClick={handleGenerate}>
                  {busy === "generate" ? "Génération…" : "Générer les contrats"}
                </Button>
              ) : <Typography className={classes.instructions}>La génération des contrats est réservée à la finance.</Typography>}
            </>
          ) : <Typography className={classes.instructions}>{generationBlockedMessage} Aucun contrat existant n'a été trouvé pour ce dossier.</Typography>}
        </Box>
      ) : (
        <Box className={classes.contractGrid}>
          {renderContractCard("sale", sale, "Contrat de vente (chauffeur)", saleBadge, "Contrat de vente et conditions générales du programme METUA'A M+.", false)}
          {guarantee
            ? renderContractCard("guarantee", guarantee, "Contrat de caution (garant)", guaranteeBadge, "Le garant signe avec un agent en agence après vérification de ses pièces et lecture du contrat.", true)
            : <Box className={classes.card}><Typography className={classes.sectionTitle}>Sans acte de caution</Typography><Typography className={classes.instructions}>Ce dossier n'a pas de contrat de caution.</Typography></Box>}

          <Box className={`${classes.card} ${classes.signatureCard}`}>
            {signing ? (
              <>
                <Box className={classes.signatureHeader}>
                  <Typography className={classes.sectionTitle}>Signature {signing.key === "sale" ? "chauffeur" : "garant"} par code SMS</Typography>
                  <CloseRoundedIcon className={classes.closeIcon} onClick={() => setSigning(null)} />
                </Box>
                <Typography className={classes.instructions}>
                  Un code de vérification a été envoyé{signing.sentTo ? ` au ${signing.sentTo}` : ""}.<br />
                  Saisissez le code pour confirmer la signature{signing.expiresAt ? ` (valable jusqu'à ${formatDateTime(signing.expiresAt)})` : ""}.
                </Typography>
                <Box className={classes.codeBoxes}>
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      ref={(element) => { inputs.current[index] = element; }}
                      value={digit}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      aria-label={`Chiffre ${index + 1}`}
                      onChange={(event) => handleDigit(index, event.target.value)}
                      onKeyDown={(event) => handleKeyDown(index, event)}
                    />
                  ))}
                </Box>
                <Box className={classes.approvalCheck}>
                  <Checkbox checked={approved} onChange={(event) => setApproved(event.target.checked)} size="small" />
                  <span>Lu et approuvé</span>
                </Box>
                <Box className={classes.signatureActions}>
                  {countdown > 0 ? (
                    <Typography className={classes.resend}><AccessTimeRoundedIcon />Renvoyer le code dans 00:{String(countdown).padStart(2, "0")}</Typography>
                  ) : (
                    <Button className={classes.linkButton} disabled={Boolean(busy)} onClick={() => requestCode(signing.key)}>Renvoyer le code</Button>
                  )}
                  <Button className={classes.confirmButton} disabled={!approved || code.join("").length !== CODE_LENGTH || Boolean(busy)} onClick={handleSign}>
                    {busy === "sign" ? "Signature…" : "Confirmer la signature"}
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Typography className={classes.sectionTitle}>Signature par code SMS</Typography>
                <Typography className={classes.instructions}>
                  {allSigned
                    ? "Tous les contrats sont signés. Vous pouvez passer à la livraison."
                    : "1. Ouvrez le PDF du contrat (la lecture est enregistrée).\n2. Envoyez le code SMS au signataire.\n3. Saisissez le code reçu pour signer."}
                </Typography>
              </>
            )}
          </Box>

          <Box className={`${classes.card} ${classes.historyCard}`}>
            <Typography className={classes.sectionTitle}>Historique</Typography>
            <Typography className={classes.instructions}>Ouvrez « Versions » sur un contrat pour voir son historique (versions générées et signées).</Typography>
            {signedVersion && (
              <Box className={classes.proofBlock}>
                <Typography className={classes.sectionTitle}>Preuve de signature</Typography>
                <Box className={classes.proofRow}>
                  <PictureAsPdfOutlinedIcon />
                  <Box><b>{sale.reference_number || "Contrat de vente"} — version signée</b><span>Ajoutée le {formatDate(signedVersion.created_at)}</span></Box>
                  <Button className={classes.downloadButton} startIcon={<DownloadOutlinedIcon />} disabled={Boolean(busy)} onClick={() => handleRead(sale, signedVersion.version_number)}>Télécharger</Button>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}

      <Drawer anchor="right" open={Boolean(history)} onClose={() => setHistory(null)}>
        <Box className={classes.drawer}>
          <Box className={classes.signatureHeader}>
            <Typography className={classes.sectionTitle}>Versions · {history?.title}</Typography>
            <IconButton size="small" aria-label="Fermer" onClick={() => setHistory(null)}><CloseRoundedIcon /></IconButton>
          </Box>
          {history?.loading ? (
            <Box className={classes.loaderBox}><CircularProgress size={24} /></Box>
          ) : history?.versions.length ? history.versions.map((version, index) => (
            <Box className={`${classes.versionRow} ${index === 0 ? classes.currentVersion : ""}`} key={version.version_number ?? index}>
              <b>v{version.version_number ?? "--"}</b>
              <span>{VERSION_KINDS[version.kind] || version.kind || "--"} · {formatDateTime(version.created_at)}</span>
              <span>{version.change_summary || "--"}</span>
              <Button className={classes.linkButton} disabled={Boolean(busy)} onClick={() => handleRead(history.contract, version.version_number)}>PDF</Button>
            </Box>
          )) : <Typography className={classes.instructions}>Aucune version disponible.</Typography>}
        </Box>
      </Drawer>

      <Box className={classes.bottomActions}>
        <Button className={classes.backButton} startIcon={<ArrowBackRoundedIcon />} onClick={onBack}>Retour au financement</Button>
        <Button className={classes.continueButton} endIcon={<ArrowForwardRoundedIcon />} disabled={!delivered && (loading || loadError || !allSigned)} onClick={onNext}>Continuer vers la livraison</Button>
      </Box>
    </Box>
  );
};

export default Contracts;
